import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import _ from 'lodash';
import { PettyCashRoles } from '../../../models/roles/roles-group';
import { Trip, TripListScope, TripSearchCommand, TripStatus, TripSummary } from '../../../models/trip/trip';
import { TripProxyService } from '../../../proxies/trip.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { TRIP_STATUSES, tripStatusLabel, tripStatusSeverity } from '../trip-status.util';

/** The filter panel's controls; select values arrive as strings. */
interface TripFilterFormModel {
  search: string | null;
  status: string | null;
}

type TripFilters = Partial<TripFilterFormModel>;

/**
 * Employee trips (#5790). Creating a trip posts nothing; money moves only through its advances and claims. Tabs:
 * every trip, the caller's own trips, and the submitted trips the caller can approve now.
 */
@Component({
  selector: 'app-trips',
  templateUrl: './trips.component.html',
})
export class TripsComponent {
  private readonly proxy = inject(TripProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly userAuth = inject(UserAuthenticationService);

  private currentSearchCriteria: TripSearchCommand = {};

  readonly canCreate = this.userAuth.roles.includes(PettyCashRoles.TripAdmin);
  readonly scopes = TripListScope;

  readonly isLoading = signal(0);
  readonly showFilters = signal(true);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'trip-filters', config: this.filterConfig });
  readonly items = signal<Trip[]>([]);
  readonly summary = signal<TripSummary | null>(null);
  readonly scope = signal<TripListScope>(TripListScope.All);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal(-1);

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as TripFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
    this.loadSummary();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('Trip_Filter_Search', 'Number, employee, destination'),
          placeholder: this.t('Trip_Filter_Search_Placeholder', 'Search by number, employee, destination or purpose'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: TRIP_STATUSES.map((status) => ({ label: tripStatusLabel(status, (k, f) => this.t(k, f)), value: String(status) })),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private searchWithFilters(filters: TripFilters): void {
    const search = (filters.search ?? '').toString().trim();
    const status = _.isNil(filters.status) || filters.status === '' ? null : (Number(filters.status) as TripStatus);
    this.currentSearchCriteria = { search: search || null, statuses: status === null ? null : [status] };
    this.currentPage.set(1);
    this.search();
  }

  /** p-tabs emits the tab's value untyped (string | number); the tabs only carry TripListScope values. */
  setScope(value: string | number | undefined): void {
    const scope = Number(value) as TripListScope;
    if (_.isNil(value) || scope === this.scope()) return;
    this.scope.set(scope);
    this.currentPage.set(1);
    this.search();
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search();
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search();
  }

  private search(): void {
    this.startLoading();
    this.proxy
      .search(
        { ...this.currentSearchCriteria, scope: this.scope() },
        this.currentPage(),
        this.pageSize(),
        this.sortField(),
        this.sortOrder(),
      )
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.items.set(response.data?.items ?? []);
        this.totalRecords.set(response.data?.totalCount ?? 0);
      });
  }

  private loadSummary(): void {
    this.proxy.getSummary().subscribe((response) => this.summary.set(response.data ?? null));
  }

  redirectToDetails(id: string): void {
    this.router.navigate(['/trip-details'], { queryParams: { id } });
  }

  createTrip(): void {
    this.redirectToDetails('new');
  }

  statusLabel(status: TripStatus): string {
    return tripStatusLabel(status, (k, f) => this.t(k, f));
  }

  statusSeverity(status: TripStatus): 'success' | 'secondary' | 'warn' | 'info' | 'danger' {
    return tripStatusSeverity(status);
  }

  getRow(rowData: unknown): Trip {
    return rowData as Trip;
  }
}
